/**
 * Singleton cross-browser Lumi playback controller.
 *
 * Guarantees:
 *  - Only one Lumi voice plays at a time (audio OR speech).
 *  - stop() cancels both HTMLAudio and Web Speech synthesis.
 *  - Playback calls are synchronous entry points that MUST be invoked from
 *    a user gesture handler (required by iOS Safari / Chrome iOS which
 *    both use WebKit and block deferred audio/speech).
 *  - Chrome/Firefox load speech voices asynchronously — we register
 *    `voiceschanged` if voices aren't ready yet.
 */

export type LumiStatus = "idle" | "loading" | "playing" | "unavailable" | "failed";

type Listener = (status: LumiStatus) => void;

function hasSpeechSynthesis(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.speechSynthesis !== "undefined" &&
    typeof SpeechSynthesisUtterance !== "undefined"
  );
}

export function isSpeechSynthesisSupported(): boolean {
  return hasSpeechSynthesis();
}

class LumiPlaybackController {
  private audio: HTMLAudioElement | null = null;
  private utter: SpeechSynthesisUtterance | null = null;
  private listeners = new Set<Listener>();
  private status: LumiStatus = "idle";
  private token = 0;

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.status);
    return () => this.listeners.delete(fn);
  }

  getStatus(): LumiStatus {
    return this.status;
  }

  private setStatus(next: LumiStatus) {
    if (this.status === next) return;
    this.status = next;
    this.listeners.forEach((fn) => {
      try { fn(next); } catch { /* ignore listener errors */ }
    });
  }

  stop(): void {
    this.token += 1;
    if (this.audio) {
      try { this.audio.pause(); } catch { /* noop */ }
      this.audio.onended = null;
      this.audio.onerror = null;
      this.audio = null;
    }
    if (hasSpeechSynthesis()) {
      try { window.speechSynthesis.cancel(); } catch { /* noop */ }
    }
    this.utter = null;
    this.setStatus("idle");
  }

  /** Play a resolved audio URL. Must be called inside a user gesture on iOS. */
  playAudioUrl(url: string): boolean {
    if (!url) return false;
    this.stop();
    const id = ++this.token;
    let audio: HTMLAudioElement;
    try {
      audio = new Audio(url);
    } catch {
      this.setStatus("failed");
      return false;
    }
    audio.preload = "auto";
    audio.volume = 0.9;
    this.audio = audio;
    audio.onended = () => {
      if (this.token === id) this.setStatus("idle");
    };
    audio.onerror = () => {
      if (this.token === id) this.setStatus("failed");
    };
    this.setStatus("playing");
    const p = audio.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        if (this.token === id) this.setStatus("failed");
      });
    }
    return true;
  }

  /**
   * Browser speech synthesis is intentionally disabled for Lumi so a
   * system voice can never impersonate her official ElevenLabs voice
   * (KR1TkIhkSykEjI4B0DtH). Kept as a no-op for API compatibility.
   */
  playSpeech(_text: string): boolean {
    if (typeof console !== "undefined") {
      console.warn(
        "[lumi-voice] playSpeech() blocked — system voice must never impersonate Lumi Vanta",
      );
    }
    this.setStatus("unavailable");
    return false;
  }

  markLoading() { this.setStatus("loading"); }
  markUnavailable() { this.setStatus("unavailable"); }
}

export const lumiPlayback = new LumiPlaybackController();

if (typeof window !== "undefined") {
  // Stop when the page is hidden / unloaded so audio never leaks across
  // navigation on any browser.
  window.addEventListener("pagehide", () => lumiPlayback.stop());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") lumiPlayback.stop();
  });
}